import React, { useContext, useState, VFC } from 'react';

import Vimeo from '@u-wave/react-vimeo';
import * as Player from '@vimeo/player';
import classNames from 'classnames';
import ReactJWPlayer from 'react-jw-player';
import YouTube from 'react-youtube';

import { staticCwaUrl } from '/configuration';
import { VideoContentJson, MintelTracking } from '/types';
import { Asset, assetUri } from '/utils/assetUri';
import { PublishStatusContext } from '/utils/contexts';
import useTracking from '/utils/useTracking';

import styles from './VideoColumn.scss';

const MINTEL_VIDEO = 'mintel-video';
const VIMEO = 'vimeo';
const YOUTUBE = 'youtube';
const JWPLAYER_LICENSE = 'sDX7WvHtH/XMxavfh1NcJg/AzIeVraQdOFvErA==';

type VideoPlayerProps = {
  blockId: string;
  blockTitle?: string;
  id: string;
  image?: string;
  trackVideo: MintelTracking['video'];
  autoplay?: boolean;
};

const JWPlayer: VFC<VideoPlayerProps> = ({
  blockId,
  blockTitle,
  id,
  image,
  trackVideo,
  autoplay = false,
}) => {
  const publishStatus = useContext(PublishStatusContext);
  const playerId = `${blockId}-${id}`;
  const videoUrl = assetUri(id, Asset.Video, publishStatus);

  const getTrackingProps = (
    jwplayer: (
      playerId: string,
    ) => {
      getVideoData: () => { title: string };
      getDuration: () => number;
      getVideoUrl: () => string;
    },
  ): Record<string, unknown> => ({
    Title: blockTitle || 'Untitled Mintel video',
    Duration: jwplayer(playerId).getDuration(),
    'Video URL': videoUrl,
  });

  return (
    <div
      onClickCapture={(e): void => {
        e.stopPropagation();
      }}
      onWheelCapture={(e): void => {
        e.stopPropagation();
      }}
      className={styles.videoContainer}
    >
      <ReactJWPlayer
        key={`${id}-${autoplay}`}
        isAutoPlay={autoplay}
        playerId={playerId}
        playerScript={`${staticCwaUrl}/effortless_content/jwplayer/jwplayer.js`}
        file={videoUrl}
        image={
          image ? assetUri(image, Asset.VideoCoverImage, publishStatus) : null
        }
        licenseKey={JWPLAYER_LICENSE}
        customProps={{ stretching: 'uniform', type: 'mp4', width: '100%' }}
        aspectRatio={'16:9'}
        className={styles.jwplayer}
        // Note `onPlay` is called only on first play
        onPlay={(): void =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          trackVideo('Play', getTrackingProps((window as any).jwplayer))
        }
        onComplete={(): void =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          trackVideo('Complete', getTrackingProps((window as any).jwplayer))
        }
      />
    </div>
  );
};

const YTPlayer: VFC<VideoPlayerProps> = ({
  id,
  trackVideo,
  autoplay = false,
}) => {
  const getTrackingProps = (player: {
    getVideoEmbedCode: () => Promise<string>;
    getDuration: () => Promise<number>;
    getVideoUrl: () => Promise<string>;
  }): Record<string, unknown> => {
    // a hacky roundabout way of getting the title from the <iframe>
    let title: string | unknown = player.getVideoEmbedCode() as unknown;
    title = new DOMParser()
      .parseFromString(title as string, 'text/html')
      .getElementsByTagName('iframe')[0]
      .getAttribute('title');

    return {
      Title: title,
      Duration: player.getDuration(),
      'Video URL': player.getVideoUrl(),
    };
  };

  return (
    <div className={styles.videoContainer}>
      <YouTube
        opts={{ playerVars: { autoplay: autoplay ? 1 : 0 } }}
        videoId={id}
        onPlay={(event): void => {
          trackVideo('Play', getTrackingProps(event.target));
        }}
        onEnd={(event): void => {
          trackVideo('Complete', getTrackingProps(event.target));
        }}
      />
    </div>
  );
};

const VimeoPlayer: VFC<VideoPlayerProps> = ({
  id,
  trackVideo,
  autoplay = false,
}) => {
  const [trackingProps, setTrackingProps] = useState({});

  // The video might not have a URL if the privacy setting on vimeo is
  // "Hide from Vimeo".
  const getVideoUrl = (player: Player): Promise<string> => {
    return player
      .getVideoUrl()
      .then((url) => url)
      .catch((error) => {
        if (error.name == 'PrivacyError') return 'hidden';
        throw error;
      });
  };

  const updateTrackingProps = async (player: Player): Promise<void> => {
    setTrackingProps({
      Title: await player.getVideoTitle(),
      Duration: await player.getDuration(),
      'Video URL': await getVideoUrl(player),
    });
  };

  return (
    /* @ts-expect-error Server Component */
    <Vimeo
      key={`${id}-${autoplay}`}
      autoplay={autoplay}
      video={id}
      className={styles.videoContainer}
      onReady={updateTrackingProps}
      onPlay={(): void => trackVideo('Play', trackingProps)}
      onEnd={(): void => trackVideo('Complete', trackingProps)}
    />
  );
};

const videoPlayerMappings: { [index: string]: VFC<VideoPlayerProps> } = {
  [MINTEL_VIDEO]: JWPlayer,
  [VIMEO]: VimeoPlayer,
  [YOUTUBE]: YTPlayer,
};

type VideoContentProps = VideoContentJson & {
  blockId: string;
  blockTitle?: string;
  autoplay?: boolean;
};

const VideoColumn: VFC<VideoContentProps> = ({
  blockId,
  blockTitle,
  hidden,
  image,
  present,
  type,
  video,
  autoplay = false,
}) => {
  const { trackVideo } = useTracking();

  let videoType = MINTEL_VIDEO;
  let videoId = video;
  if (type !== MINTEL_VIDEO) {
    const urlTokens = video.split('.').slice(0, 3);
    videoId = (video.split('/').pop() || '').split('?')[0];
    if (urlTokens.includes(YOUTUBE)) {
      videoType = YOUTUBE;
    } else if (urlTokens.includes(VIMEO)) {
      videoType = VIMEO;
    }
  }

  const Player = videoPlayerMappings[videoType];
  return (
    <>
      {!hidden && video && (
        <div className={classNames({ [styles.present]: present })}>
          <Player
            blockId={blockId}
            blockTitle={blockTitle}
            id={videoId}
            image={image}
            trackVideo={trackVideo}
            autoplay={autoplay}
          />
        </div>
      )}
    </>
  );
};

export default VideoColumn;
